import type { SelectProps } from '@mantine/core';
import { Select, Textarea } from '@mantine/core';
import { IconBolt, IconBrain, IconBrowser, IconBrowserOff, IconViewportTall } from '@tabler/icons-react';
import { cn } from '~/common/string/classname';
import {
  type AIAssertionModel,
  type AIAssertionScreenshotType,
  zAIAssertionModel,
  zAIAssertionScreenshotType,
} from '~/server/logic/steps/operation/types';

type SelectOptionProps = {
  label: string;
  icon: React.ElementType;
  description: string;
};

// Screenshot type options
const SCREENSHOT_OPTIONS: Record<AIAssertionScreenshotType | 'DISABLED', SelectOptionProps> = {
  DISABLED: {
    label: 'Disabled',
    icon: IconBrowserOff,
    description: 'Fastest and recommended for asserting variable values',
  },
  VIEWPORT: {
    label: 'Viewport',
    icon: IconBrowser,
    description: 'Screenshot of visible area only',
  },
  FULL_PAGE: {
    label: 'Extended',
    icon: IconViewportTall,
    description: 'Full page screenshot',
  },
};

// Model options
const MODEL_OPTIONS: Record<AIAssertionModel, SelectOptionProps> = {
  STABLY_FLASH: {
    label: 'Stably Flash',
    icon: IconBolt,
    description: 'Fast and recommended for simple assertions',
  },
  STABLY_DEEP_THINK: {
    label: 'Stably Deep Think',
    icon: IconBrain,
    description: 'Recommended for complex assertions',
  },
};

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
  // We know the option will be in one of these 2, so this "unsafe" code is fine
  const screenshotOption =
    option.value in SCREENSHOT_OPTIONS ?
      SCREENSHOT_OPTIONS[option.value as AIAssertionScreenshotType]
    : MODEL_OPTIONS[option.value as AIAssertionModel];
  return (
    <div>
      <div className="flex items-center gap-2">
        <screenshotOption.icon size={14} />
        <span className="text-xs">{option.label}</span>
      </div>
      <span className="text-[12px] text-gray-500">{screenshotOption.description}</span>
    </div>
  );
};

const LABEL_STYLE = 'text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';

export function AIAssertionConfig({
  assertion,
  includeScreenshot,
  screenshotType,
  model,
  onAssertionChange,
  onIncludeScreenshotChange,
  onScreenshotTypeChange,
  onModelChange,
  className,
  textareaProps,
  readonly,
}: {
  assertion: string;
  includeScreenshot: boolean;
  screenshotType: AIAssertionScreenshotType;
  model: AIAssertionModel;
  onAssertionChange: (value: string) => void;
  onIncludeScreenshotChange: (value: boolean) => void;
  onScreenshotTypeChange: (value: AIAssertionScreenshotType) => void;
  onModelChange: (value: AIAssertionModel) => void;
  className?: string;
  promptTitle?: string;
  textareaProps?: React.ComponentProps<typeof Textarea>;
  readonly?: boolean;
}) {
  const selectedScreenshotOption = SCREENSHOT_OPTIONS[includeScreenshot ? screenshotType : 'DISABLED'];
  const selectedModelOption = MODEL_OPTIONS[model];

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <Textarea
        label="Assertion Prompt"
        placeholder="e.g. Verify the user's name is displayed in the header"
        autosize
        minRows={2}
        value={assertion}
        onChange={(e) => onAssertionChange(e.currentTarget.value)}
        // Submit on Enter
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const { form } = e.currentTarget;
            if (form) {
              form.requestSubmit();
            }
          }
        }}
        readOnly={readonly}
        classNames={{ label: LABEL_STYLE }}
        {...textareaProps}
      />

      <Select
        label="Screenshot type"
        data={Object.entries(SCREENSHOT_OPTIONS).map(([key, option]) => ({
          value: key,
          label: option.label,
        }))}
        value={includeScreenshot ? screenshotType : 'DISABLED'}
        disabled={readonly}
        leftSection={<selectedScreenshotOption.icon size={14} />}
        comboboxProps={{ withinPortal: false }}
        renderOption={renderSelectOption}
        classNames={{ label: LABEL_STYLE }}
        allowDeselect={false}
        onOptionSubmit={(value) => {
          if (value === 'DISABLED') {
            onIncludeScreenshotChange(false);
          } else {
            onIncludeScreenshotChange(true);
            onScreenshotTypeChange(zAIAssertionScreenshotType.parse(value));
          }
        }}
      />

      <Select
        label="Model"
        data={Object.entries(MODEL_OPTIONS).map(([key, option]) => ({
          value: key,
          label: option.label,
        }))}
        value={model}
        disabled={readonly}
        leftSection={<selectedModelOption.icon size={14} />}
        comboboxProps={{ withinPortal: false }}
        renderOption={renderSelectOption}
        classNames={{ label: LABEL_STYLE }}
        allowDeselect={false}
        onOptionSubmit={(value) => onModelChange(zAIAssertionModel.parse(value))}
      />
    </div>
  );
}
